// 바닥 드롭·줍기·가방·강화 장부 데이터(정본: docs/CONTENT-90D-PRD.md 4·4.2·4.3·4.7, D2a·D2b). 데이터만 — 규칙은 shared/gear.ts·gearLedger.ts.
// 수치 근거: 드롭 확률·등급은 world.ts DROP_TABLES(D0a 확정 + c009 등급 풀 분리), 강화는 items.ts ENHANCE_TABLE·economy.ts 비용.
import type { RegionId } from '../levels';
import type { AnyStageId, Difficulty, RegionNo, StageId } from './types';

/** 게임 지도(지역) → 콘텐츠 지역·기본 난이도(아이템 레벨·강화석 문맥). 하드 판은 판 계획 diff가 이 값을 덮는다(D3b, game/loot.ts) */
export const LOOT_MAP: Readonly<Record<RegionId, { readonly region: RegionNo; readonly diff: Difficulty }>> = {
  r1: { region: 1, diff: 'normal' },
  r2: { region: 2, diff: 'normal' },
  r3: { region: 3, diff: 'normal' },
  r4: { region: 4, diff: 'normal' },
  r5: { region: 5, diff: 'normal' },
  r6: { region: 6, diff: 'normal' },
};
/**
 * 판의 드롭 스테이지(D3a, c015 근사 대체). 스테이지 판은 실제로 들어온 스테이지 그대로다: 일반 웨이브·엘리트는 그 스테이지 표
 * (drop_normal·drop_mid·drop_final)의 gradeWeights, 보스 확정 드롭 등급도 그 표 — 중간보스 표는 n-5, 지역보스 표(전설 3%)는 n-10 판에서만.
 * 그래서 1-1~1-10을 한 번씩 돌면 판당 전설 기대값이 시뮬 perKillDrops(10)과 같다(loot.test). 이전 c015는 스테이지가 없어 '맵 클리어 수 mod 10 + 1'로 근사했다.
 * 자유 입장(스테이지를 고르지 않은 명령·QA 하네스)은 그 지역에서 지금 들어갈 스테이지(고른 스테이지·최전선, 잠긴 지역은 1스테이지)의 표를 쓴다.
 * 첫 판은 1스테이지라 처음부터 전설·확정 장비를 받지 않는다(c009 Codex P2와 같은 원칙).
 */
export function lootStageFor(plan: { readonly stage: AnyStageId | null; readonly region: RegionId }, progress: { selected(region: RegionId): string | null }): StageId {
  // 하드 판(D3b, h…)은 같은 자리 노말 스테이지의 표(일반·중간보스·지역보스)를 쓴다 — 난이도는 드롭 문맥(diff)이 아이템 레벨·강화석·보석에 붙인다
  if (plan.stage) return (plan.stage.charCodeAt(0) === 104 ? 'r' + plan.stage.slice(1) : plan.stage) as StageId;
  const sel = progress.selected(plan.region);
  return (sel ?? `r${LOOT_MAP[plan.region].region}-s01`) as StageId;
}

/**
 * 보스 처치의 최소 확정 장비 수(c015 조정, D3a 유지): 보스는 어느 판이든 확정 장비 1개 이상을 준다(보스 처치 보상 체감 유지).
 * 등급은 그 판 스테이지 표의 bossGradeWeights — 중간보스(drop_mid)는 전설 0, 지역보스(drop_final)만 전설을 굴린다.
 * D3a 스테이지 판에서는 보스가 n-5·n-10에만 나오므로(일반 스테이지는 아레나 수비대) 10스테이지 보스 확정 장비는 1 + 2 = 3개, 전설 기대값은 시뮬과 같다
 */
export const LIVE_BOSS_MIN_DROPS = 1;
/** 그 판 스테이지 표의 보스 확정 수와 최소 확정 수 중 큰 쪽 */
export function liveBossDrops(guaranteed: number): number { return Math.max(LIVE_BOSS_MIN_DROPS, guaranteed); }

/** 바닥 드롭 풀 크기(고정, 프레임 루프 할당 없음). 넘치면 가장 오래된 드롭을 바로 가방에 넣는다 */
export const LOOT_POOL = 48;
/** 영웅이 걸어서 줍는 반경(m, 영웅 중심~드롭) */
export const HERO_PICKUP_RADIUS = 1.1;
/** 떨어진 뒤 줍기까지 기다리는 틱(튀어나오는 연출 0.33초, 몹 위에 서 있어도 바로 빨려 들지 않게) */
export const LOOT_ARM_TICKS = 20;
/** 드롭이 몹 자리에서 흩어지는 최대 거리(m) */
export const LOOT_SCATTER = 0.9;
/**
 * 펫 자동 줍기(PRD 4.7 반경 4m). 펫 시스템(동행·진화)이 아직 없어 줍기 훅만 있다: 동행 펫 id를 주면 그 펫의 pickupRadius(content/items PETS)로
 * 줍는다. 펫 데이터의 반경과 같은 값을 쓰는지 테스트가 확인한다.
 */
export const PET_PICKUP_DEFAULT = 4;
/** 판을 클리어하면 바닥에 남은 드롭을 모두 거둔다(모바일 핵앤슬래시 관례, 결과 화면 전에) */
export const SWEEP_ON_CLEAR = true;

/** 가방 칸(장착 6칸은 따로). 가득 차면 새로 줍는 장비는 그 자리에서 자동 정리(일반·희귀 판매, 영웅 이상 분해) */
export const BAG_CAP = 60;
/** 최근 적용 연산 id를 기억하는 수(중복 적용 방지 창). 탭 재시도·재로드 재전송을 막는다 */
export const APPLIED_CAP = 512;
/** 보상 카드 지급 연산 id 접두사(cardReward.cardGrantOp)와, 일반 연산과 따로 기억하는 수(gearLedger.trimApplied) */
export const CARD_APPLIED_PREFIX = 'cd:';
export const CARD_APPLIED_CAP = 256;
/** 탭별 적용 수위표 보관 수(최근에 쓴 탭부터). 이보다 오래된 탭은 applied 목록으로만 중복을 막는다 */
export const MARKS_CAP = 128;
/** 강화 장부(최근 기록, 표시 전용) 보관 수. 120 → 40: 720h 끝 저장이 상한 256KB를 넘어 줄였다(dod D10, C6 실측 약 −10KB) */
export const ENHANCE_LOG_CAP = 40;
/** 이 탭이 쓴 연산을 다른 탭 덮어쓰기 뒤 다시 확인하는 수(APPLIED_CAP보다 충분히 작아야 한다) */
export const OWN_OPS_CAP = 64;
/** 골드 상점 보호 주문서 주간 한도(PRD 4.3 골드 상점 주 2장) — 수량은 items.PROTECT_WEEKLY.goldShop */
export const WEEK_MS = 7 * 24 * 3600 * 1000;
/** 주 번호 기준(2026-01-05 월요일 00:00 KST). 주는 월요일 00:00 KST에 바뀐다 */
export const WEEK_EPOCH_MS = Date.UTC(2026, 0, 4, 15, 0, 0);
export function weekIndex(nowMs: number): number { return Math.floor((nowMs - WEEK_EPOCH_MS) / WEEK_MS); }

/** 등급별 바닥 빛기둥 색(PRD 4.2 표 색). 렌더·UI가 같은 값을 쓴다 */
export const GRADE_BEAM: Readonly<Record<'common' | 'rare' | 'epic' | 'set' | 'legend', number>> = {
  common: 0xe8e8e8, rare: 0x4a8cff, epic: 0xa45cff, set: 0x3fd07a, legend: 0xf2b233,
};
/** 이름 있는 유니크 빛기둥 색(items.NAMED_COLOR #ff8c2e와 같은 값 — 전설 금색과 갈리는 주황). 렌더 표의 등급 칸 5 */
export const NAMED_BEAM = 0xff8c2e;
/** 렌더·VFX 표에서 이름 있는 유니크가 쓰는 칸(등급 번호 0..4 다음) */
export const NAMED_FX_INDEX = 5;
/** 등급 번호(이벤트·풀 배열용, GRADES 순서) */
export const GRADE_INDEX = { common: 0, rare: 1, epic: 2, set: 3, legend: 4 } as const;
